import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Notification from '../../components/Notification';
import {
  submitSummitRegistration,
  generateCalendarICS,
  SUMMIT_EVENT_DETAILS
} from '../../services/summitService';
import './SummitRegister.css';
import summitHero from '../../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/REMS0044.jpg';
import summitFlyer from '../../assets/Flyer.jpeg';

const REGISTRATION_TYPES = [
  {
    id: 'delegate',
    name: 'General Delegate',
    tag: '2,000 CFA',
    desc: 'Students, graduates, young professionals, and innovators attending the 2-day Summit sessions and Career Studio.',
    icon: '🎓'
  },
  {
    id: 'challenge',
    name: 'Innovation Challenge Team',
    tag: 'HACK NIGHT',
    desc: 'Compete in the overnight Hack Night innovation sprint for prizes, incubation, and mentorship.',
    icon: '💡'
  },
  {
    id: 'scholarship',
    name: 'Summit Scholarship Applicant',
    tag: 'FINANCIAL AID',
    desc: 'Apply for financial assistance covering delegate access, travel support, and accommodations.',
    icon: '🌟'
  },
  {
    id: 'exhibitor',
    name: 'Exhibitor / Partner Rep',
    tag: 'ORGANIZATIONS',
    desc: 'Book an exhibition booth or represent a partner company, university, or developmental organization.',
    icon: '🏢'
  }
];

const SummitRegister = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'delegate';

  const [formData, setFormData] = useState({
    registrationType: ['delegate', 'challenge', 'scholarship', 'exhibitor'].includes(initialType)
      ? initialType
      : 'delegate',
    fullName: '',
    email: '',
    phone: '',
    city: 'Bamenda',
    country: 'Cameroon',
    category: 'Student',
    organization: '',
    trackInterest: 'Employability & Career Readiness',
    teamName: '',
    teamSize: '1',
    isTeamLead: false,
    projectIdea: '',
    scholarshipStatement: '',
    dietaryOrAccessibility: '',
    referralSource: 'Social Media',
    notes: '',
    paymentConfirmed: false,
    agreedToTerms: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [completedRegistration, setCompletedRegistration] = useState(null);
  const [showFlyer, setShowFlyer] = useState(true);

  useEffect(() => {
    const typeFromQuery = searchParams.get('type');
    if (typeFromQuery && ['delegate', 'challenge', 'scholarship', 'exhibitor'].includes(typeFromQuery)) {
      setFormData(prev => ({ ...prev, registrationType: typeFromQuery }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTypeSelect = (typeId) => {
    setFormData(prev => ({ ...prev, registrationType: typeId }));
  };

  const showNotification = (message, type = 'info') => setNotification({ show: true, message, type });
  const hideNotification = () => setNotification({ show: false, message: '', type: 'info' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setIsSubmitting(true);

    try {
      const result = await submitSummitRegistration(formData);
      if (result.success) {
        setCompletedRegistration(result.record);
        showNotification(result.message, 'success');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        if (result.errors) {
          setFormErrors(result.errors);
        }
        showNotification(result.message, 'error');
      }
    } catch {
      showNotification('An unexpected error occurred. Please try again or contact us directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintBadge = () => {
    window.print();
  };

  return (
    <div className="summit-register-page">
      <SEO
        title="Register for YOCILAB Youth Summit 2026 | 30th & 31st October"
        description="Register for the YOCILAB Youth Summit 2026 in Bamenda, Cameroon on 30th and 31st of October. Join 500+ young leaders, employers, and innovators."
      />

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.show}
        onClose={hideNotification}
        duration={6000}
      />

      {showFlyer && !completedRegistration && (
        <div className="registration-flyer-modal" role="dialog" aria-modal="true" aria-labelledby="registration-flyer-title">
          <button
            type="button"
            className="registration-flyer-modal__backdrop"
            aria-label="Close summit flyer"
            onClick={() => setShowFlyer(false)}
          />
          <div className="registration-flyer-modal__content">
            <button
              type="button"
              className="registration-flyer-modal__close"
              aria-label="Close summit flyer"
              onClick={() => setShowFlyer(false)}
            >
              <span aria-hidden="true">×</span>
            </button>
            <h2 id="registration-flyer-title" className="registration-flyer-modal__title">YOCILAB Youth Summit 2026</h2>
            <img
              src={summitFlyer}
              alt="YOCILAB Youth Summit 2026 registration flyer"
              className="registration-flyer-modal__image"
            />
            <button
              type="button"
              className="registration-flyer-modal__action"
              onClick={() => setShowFlyer(false)}
            >
              Continue to registration
            </button>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <section className="reg-hero">
        <div className="reg-hero__bg">
          <img src={summitHero} alt="YOCILAB Youth Summit 2026" className="reg-hero__img" />
          <div className="reg-hero__overlay" />
        </div>
        <div className="container">
          <div className="reg-hero__badge">
            <span className="reg-hero__badge-dot" />
            OFFICIAL REGISTRATION PORTAL
          </div>
          <h1 className="reg-hero__title">
            YOCILAB Youth Summit 2026<br />
            <em>Delegate &amp; Participant Registration</em>
          </h1>
          <div className="reg-hero__chips">
            <span className="reg-chip">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <strong>Dates: 30th and 31st of October 2026</strong>
            </span>
            <span className="reg-chip">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Bamenda, Cameroon
            </span>
            <span className="reg-chip">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
              500+ Expected Delegates
            </span>
          </div>
        </div>
      </section>

      {/* Main Container */}
      <section className="reg-main-section">
        <div className="container">
          {completedRegistration ? (
            /* SUCCESS / CONFIRMATION BADGE VIEW */
            <div className="reg-success-card">
              <div className="reg-success-header">
                <div className="reg-success-icon">✓</div>
                <h2>Registration Confirmed!</h2>
                <p>You are officially registered for the <strong>YOCILAB Youth Summit 2026</strong>.</p>
              </div>

              {/* Digital Pass Card */}
              <div className="digital-badge" id="printable-badge">
                <div className="digital-badge__top">
                  <div className="digital-badge__brand">
                    <span className="digital-badge__org">YOCILAB</span>
                    <span className="digital-badge__event">YOUTH SUMMIT 2026</span>
                  </div>
                  <div className="digital-badge__status">CONFIRMED PASS</div>
                </div>

                <div className="digital-badge__body">
                  <h3 className="digital-badge__name">{completedRegistration.fullName}</h3>
                  <div className="digital-badge__meta-grid">
                    <div>
                      <span className="badge-meta-label">Delegate ID</span>
                      <span className="badge-meta-value badge-meta-id">{completedRegistration.registrationId}</span>
                    </div>
                    <div>
                      <span className="badge-meta-label">Pass Category</span>
                      <span className="badge-meta-value badge-meta-type">
                        {REGISTRATION_TYPES.find(t => t.id === completedRegistration.registrationType)?.name || 'Delegate'}
                      </span>
                    </div>
                    <div>
                      <span className="badge-meta-label">Event Dates</span>
                      <span className="badge-meta-value">30th &amp; 31st October 2026</span>
                    </div>
                    <div>
                      <span className="badge-meta-label">City / Location</span>
                      <span className="badge-meta-value">{completedRegistration.city}, {completedRegistration.country}</span>
                    </div>
                    <div>
                      <span className="badge-meta-label">Affiliation</span>
                      <span className="badge-meta-value">{completedRegistration.organization}</span>
                    </div>
                    <div>
                      <span className="badge-meta-label">Primary Interest</span>
                      <span className="badge-meta-value">{completedRegistration.trackInterest}</span>
                    </div>
                  </div>
                </div>

                <div className="digital-badge__footer">
                  <span>📍 Bamenda, Cameroon</span>
                  <span>🗓️ 30 – 31 Oct 2026</span>
                  <span>⚡ Connecting Talent to Opportunity</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="reg-success-actions">
                <button type="button" onClick={handlePrintBadge} className="btn-badge-action btn-badge-print">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Print / Save Delegate Pass
                </button>
                <button type="button" onClick={generateCalendarICS} className="btn-badge-action btn-badge-cal">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Add to Calendar (Oct 30-31)
                </button>
                <a
                  href="https://chat.whatsapp.com/GoTOvyXjhjk0Nln2LPfsjm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-badge-action btn-badge-whatsapp"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.001 2C6.478 2 2 6.477 2 12c0 1.876.513 3.633 1.406 5.145L2 22l4.994-1.311A9.955 9.955 0 0012.001 22C17.523 22 22 17.523 22 12S17.523 2 12.001 2zm0 18.2a8.169 8.169 0 01-4.166-1.14l-.299-.177-2.965.778.792-2.891-.194-.297A8.173 8.173 0 013.8 12c0-4.53 3.686-8.2 8.201-8.2 4.516 0 8.2 3.67 8.2 8.2 0 4.53-3.684 8.2-8.2 8.2z"/></svg>
                  Join Summit WhatsApp Community
                </a>
              </div>

              <div className="reg-success-reset">
                <button
                  type="button"
                  onClick={() => {
                    setCompletedRegistration(null);
                    setFormData(prev => ({ ...prev, fullName: '', email: '', phone: '', paymentConfirmed: false, agreedToTerms: false }));
                  }}
                  className="btn-link-reset"
                >
                  ← Register another person
                </button>
                <Link to="/youth-summit" className="btn-link-home">
                  Return to Summit Overview
                </Link>
              </div>
            </div>
          ) : (
            /* REGISTRATION FORM VIEW */
            <div className="reg-layout">
              {/* Left Column: Key Highlights & Details */}
              <div className="reg-sidebar">
                <div className="reg-info-box">
                  <span className="reg-info-tag">SUMMIT DATES</span>
                  <h3 className="reg-info-heading">30th &amp; 31st October 2026</h3>
                  <p className="reg-info-text">
                    The 2-day flagship event in Bamenda, Cameroon bringing together 500+ delegates, 25+ organizations, and top African innovators.
                  </p>
                </div>

                <div className="reg-perks-card">
                  <h4 className="reg-perks-title">What your registration includes:</h4>
                  <ul className="reg-perks-list">
                    <li>
                      <span className="perk-check">✓</span>
                      <div>
                        <strong>Full Access to Main Stage:</strong> Inspiring keynotes, policy panels, and youth visionaries.
                      </div>
                    </li>
                    <li>
                      <span className="perk-check">✓</span>
                      <div>
                        <strong>Career Studio &amp; Recruiter Corner:</strong> CV reviews, LinkedIn audits, and direct job/internship matching.
                      </div>
                    </li>
                    <li>
                      <span className="perk-check">✓</span>
                      <div>
                        <strong>Opportunity Marketplace:</strong> Connect with 25+ university, scholarship, and grant exhibitors.
                      </div>
                    </li>
                    <li>
                      <span className="perk-check">✓</span>
                      <div>
                        <strong>Future Skills Masterclasses:</strong> Practical AI, entrepreneurship, and digital transformation workshops.
                      </div>
                    </li>
                    <li>
                      <span className="perk-check">✓</span>
                      <div>
                        <strong>Fellowship Eligibility:</strong> Priority consideration for the 6-month YOCILAB Fellowship cohort.
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="reg-contact-card">
                  <h4>Have a question or group booking?</h4>
                  <p>Our team is available to assist you.</p>
                  <a href="mailto:summit@yocilab.org" className="reg-contact-link">
                    ✉ summit@yocilab.org
                  </a>
                  <a href="tel:+237680192095" className="reg-contact-link">
                    📞 +237 680 192 095
                  </a>
                </div>
              </div>

              {/* Right Column: Interactive Multi-Track Form */}
              <div className="reg-form-panel">
                <form className="reg-form" onSubmit={handleSubmit} noValidate>
                  {/* Step 1: Select Track */}
                  <div className="reg-section">
                    <label className="reg-section-label">
                      Step 1: Choose Your Registration Track <span className="form-required">*</span>
                    </label>
                    <div className="reg-type-grid">
                      {REGISTRATION_TYPES.map((type) => (
                        <div
                          key={type.id}
                          className={`reg-type-card ${formData.registrationType === type.id ? 'active' : ''}`}
                          onClick={() => handleTypeSelect(type.id)}
                        >
                          <div className="reg-type-header">
                            <span className="reg-type-icon">{type.icon}</span>
                            <span className="reg-type-tag">{type.tag}</span>
                          </div>
                          <h4 className="reg-type-title">{type.name}</h4>
                          <p className="reg-type-desc">{type.desc}</p>
                          <div className="reg-type-radio">
                            <input
                              type="radio"
                              name="registrationType"
                              value={type.id}
                              checked={formData.registrationType === type.id}
                              onChange={() => handleTypeSelect(type.id)}
                            />
                            <span>Select this track</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Personal Information */}
                  <div className="reg-section">
                    <h3 className="reg-section-title">Step 2: Delegate Information</h3>

                    <div className="reg-row">
                      <div className="form-group">
                        <label htmlFor="fullName" className="form-label">
                          Full Name <span className="form-required">*</span>
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="e.g. Marie Claire Lum"
                          className={`form-input ${formErrors.fullName ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.fullName && <span className="form-error">{formErrors.fullName}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email Address <span className="form-required">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="marie@example.com"
                          className={`form-input ${formErrors.email ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                      </div>
                    </div>

                    <div className="reg-row">
                      <div className="form-group">
                        <label htmlFor="phone" className="form-label">
                          Phone / WhatsApp Number <span className="form-required">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+237 6XX XXX XXX"
                          className={`form-input ${formErrors.phone ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.phone && <span className="form-error">{formErrors.phone}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="city" className="form-label">
                          City / Location <span className="form-required">*</span>
                        </label>
                        <select
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className={`form-input ${formErrors.city ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        >
                          <option value="Bamenda">Bamenda, Cameroon</option>
                          <option value="Yaoundé">Yaoundé, Cameroon</option>
                          <option value="Douala">Douala, Cameroon</option>
                          <option value="Buea">Buea, Cameroon</option>
                          <option value="Bafoussam">Bafoussam, Cameroon</option>
                          <option value="Limbe">Limbe, Cameroon</option>
                          <option value="Other Cameroon">Other Region (Cameroon)</option>
                          <option value="International">International / Outside Cameroon</option>
                        </select>
                        {formErrors.city && <span className="form-error">{formErrors.city}</span>}
                      </div>
                    </div>

                    <div className="reg-row">
                      <div className="form-group">
                        <label htmlFor="category" className="form-label">
                          Your Current Status <span className="form-required">*</span>
                        </label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="form-input"
                          disabled={isSubmitting}
                        >
                          <option value="Student">Student (University / Secondary / Vocational)</option>
                          <option value="Graduate">Recent Graduate / Job Seeker</option>
                          <option value="Entrepreneur">Entrepreneur / Founder</option>
                          <option value="Tech/Creative">Tech / Creative Professional</option>
                          <option value="Educator">Educator / Academic</option>
                          <option value="NGO/Civil Society">NGO / Civil Society Representative</option>
                          <option value="Corporate/Partner">Corporate / Investor / Partner</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="organization" className="form-label">
                          School / University / Organization <span className="form-required">*</span>
                        </label>
                        <input
                          type="text"
                          id="organization"
                          name="organization"
                          value={formData.organization}
                          onChange={handleChange}
                          placeholder="e.g. University of Bamenda, or Freelance"
                          className={`form-input ${formErrors.organization ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.organization && <span className="form-error">{formErrors.organization}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="trackInterest" className="form-label">
                        Primary Area of Interest
                      </label>
                      <select
                        id="trackInterest"
                        name="trackInterest"
                        value={formData.trackInterest}
                        onChange={handleChange}
                        className="form-input"
                        disabled={isSubmitting}
                      >
                        <option value="Employability & Career Readiness">Employability &amp; Career Readiness (Career Studio)</option>
                        <option value="Innovation & Technology (AI, Software)">Innovation &amp; Technology (AI, Software, Digital Tools)</option>
                        <option value="Entrepreneurship & Startup Funding">Entrepreneurship &amp; Startup Funding</option>
                        <option value="Leadership & Civic Impact">Youth Leadership &amp; Civic Impact</option>
                        <option value="Global Scholarships & Fellowships">Global Scholarships &amp; Fellowships Hub</option>
                      </select>
                    </div>
                  </div>

                  {/* Conditional Step for Innovation Challenge */}
                  {formData.registrationType === 'challenge' && (
                    <div className="reg-section reg-section--special">
                      <h3 className="reg-section-title">💡 Innovation Challenge (Hack Night) Details</h3>
                      <p className="reg-section-desc">
                        Free for all Summit delegates. Form a multidisciplinary team or join as an individual innovator to solve community challenges.
                      </p>

                      <div className="reg-row">
                        <div className="form-group">
                          <label htmlFor="teamName" className="form-label">
                            Team Name (or specify "Individual") <span className="form-required">*</span>
                          </label>
                          <input
                            type="text"
                            id="teamName"
                            name="teamName"
                            value={formData.teamName}
                            onChange={handleChange}
                            placeholder="e.g. AgriTech Pioneers"
                            className={`form-input ${formErrors.teamName ? 'form-input-error' : ''}`}
                            disabled={isSubmitting}
                          />
                          {formErrors.teamName && <span className="form-error">{formErrors.teamName}</span>}
                        </div>

                        <div className="form-group">
                          <label htmlFor="teamSize" className="form-label">
                            Team Size
                          </label>
                          <select
                            id="teamSize"
                            name="teamSize"
                            value={formData.teamSize}
                            onChange={handleChange}
                            className="form-input"
                            disabled={isSubmitting}
                          >
                            <option value="1">1 (Individual seeking team matching)</option>
                            <option value="2">2 Members</option>
                            <option value="3">3 Members</option>
                            <option value="4">4 Members</option>
                            <option value="5">5 Members (Maximum team size)</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="projectIdea" className="form-label">
                          Project / Idea Concept or Focus Area <span className="form-required">*</span>
                        </label>
                        <textarea
                          id="projectIdea"
                          name="projectIdea"
                          rows="3"
                          value={formData.projectIdea}
                          onChange={handleChange}
                          placeholder="Briefly describe what problem your solution addresses (e.g. Agriculture, Clean Energy, Health, Education, FinTech)..."
                          className={`form-textarea ${formErrors.projectIdea ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.projectIdea && <span className="form-error">{formErrors.projectIdea}</span>}
                      </div>
                    </div>
                  )}

                  {/* Conditional Step for Summit Scholarship */}
                  {formData.registrationType === 'scholarship' && (
                    <div className="reg-section reg-section--special">
                      <h3 className="reg-section-title">🌟 Summit Scholarship Application</h3>
                      <p className="reg-section-desc">
                        Scholarships are awarded to ambitious young changemakers demonstrating leadership, community impact, or financial need.
                      </p>

                      <div className="form-group">
                        <label htmlFor="scholarshipStatement" className="form-label">
                          Statement of Need &amp; Potential Impact <span className="form-required">*</span>
                        </label>
                        <textarea
                          id="scholarshipStatement"
                          name="scholarshipStatement"
                          rows="4"
                          value={formData.scholarshipStatement}
                          onChange={handleChange}
                          placeholder="Tell us about yourself, why you need financial support to attend the Summit in Bamenda, and how you will use this opportunity to create impact in your community..."
                          className={`form-textarea ${formErrors.scholarshipStatement ? 'form-input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                        {formErrors.scholarshipStatement && <span className="form-error">{formErrors.scholarshipStatement}</span>}
                      </div>
                    </div>
                  )}

                  {/* Conditional Step for Exhibitors / Partners */}
                  {formData.registrationType === 'exhibitor' && (
                    <div className="reg-section reg-section--special">
                      <h3 className="reg-section-title">🏢 Exhibitor &amp; Partner Stand Request</h3>
                      <p className="reg-section-desc">
                        Connect directly with 500+ motivated young people to showcase opportunities, courses, products, or job openings.
                      </p>

                      <div className="form-group">
                        <label htmlFor="notes" className="form-label">
                          What will your organization showcase or recruit for?
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          rows="3"
                          value={formData.notes}
                          onChange={handleChange}
                          placeholder="Tell us about your organization's goals for the Opportunity Marketplace stand..."
                          className="form-textarea"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Logistics & Referral */}
                  <div className="reg-section">
                    <div className="reg-row">
                      <div className="form-group">
                        <label htmlFor="referralSource" className="form-label">
                          How did you hear about the Summit?
                        </label>
                        <select
                          id="referralSource"
                          name="referralSource"
                          value={formData.referralSource}
                          onChange={handleChange}
                          className="form-input"
                          disabled={isSubmitting}
                        >
                          <option value="Campus Tour">YOCILAB Campus Tour visit</option>
                          <option value="Social Media">Social Media (LinkedIn, Facebook, Instagram, Twitter)</option>
                          <option value="WhatsApp">WhatsApp Group / Community</option>
                          <option value="Friend/Colleague">Friend, Colleague or Mentor</option>
                          <option value="University/School">University / School Announcement</option>
                          <option value="Partner Org">Partner Organization</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="dietaryOrAccessibility" className="form-label">
                          Special Requirements (Optional)
                        </label>
                        <input
                          type="text"
                          id="dietaryOrAccessibility"
                          name="dietaryOrAccessibility"
                          value={formData.dietaryOrAccessibility}
                          onChange={handleChange}
                          placeholder="Dietary or accessibility needs"
                          className="form-input"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    {formData.registrationType !== 'scholarship' && (
                      <div className="payment-instructions">
                        <div className="payment-instructions__heading">
                          <span className="payment-instructions__icon">₣</span>
                          <div>
                            <h3>Step 4: Confirm Your Registration Payment</h3>
                            <p>Pay <strong>2,000 CFA</strong> by Mobile Money, then send the payment screenshot on WhatsApp.</p>
                          </div>
                        </div>
                        <div className="payment-instructions__details">
                          <span>Payment number</span>
                          <strong>+237 670 059 101</strong>
                          <span>MoMo name</span>
                          <strong>Safeh Emmanuela Manjoah</strong>
                        </div>
                        <p className="payment-instructions__note">
                          Need to pay later? Join the community now, but your summit registration will only be confirmed after payment.
                        </p>
                        <a
                          href="https://chat.whatsapp.com/GoTOvyXjhjk0Nln2LPfsjm"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="payment-instructions__community"
                        >
                          Join the YOCILAB Summit WhatsApp Community →
                        </a>
                        <label className="checkbox-label payment-confirmation">
                          <input
                            type="checkbox"
                            name="paymentConfirmed"
                            checked={formData.paymentConfirmed}
                            onChange={handleChange}
                            disabled={isSubmitting}
                          />
                          <span>I have paid the 2,000 CFA registration fee and sent the payment screenshot to the WhatsApp number above. <span className="form-required">*</span></span>
                        </label>
                        {formErrors.paymentConfirmed && <span className="form-error">{formErrors.paymentConfirmed}</span>}
                      </div>
                    )}

                    {/* Agreement & Confirmation */}
                    <div className="form-group form-checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="agreedToTerms"
                          checked={formData.agreedToTerms}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                        <span>
                          I confirm my attendance on <strong>30th &amp; 31st October 2026</strong> in Bamenda, Cameroon and agree to adhere to the Summit participant guidelines. <span className="form-required">*</span>
                        </span>
                      </label>
                      {formErrors.agreedToTerms && <span className="form-error">{formErrors.agreedToTerms}</span>}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="reg-submit-wrap">
                    <button
                      type="submit"
                      className={`reg-submit-btn ${isSubmitting ? 'loading' : ''}`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Processing Registration...' : 'Complete Summit Registration →'}
                    </button>
                    <p className="reg-submit-note">
                      🔒 Instant confirmation · Your delegate pass will be generated upon submission.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SummitRegister;
